import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Collapse from '@mui/material/Collapse';
import { useLocation, useNavigate } from 'react-router-dom';
import TopNavigationBar from './TopNavigationBar';
import {
	Button,
	Grid2,
	IconButton,
	Tooltip,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { ChevronDownIcon, CloseIcon } from '../assets/icons/Icons';
import { MAIN_THEME_COLOR } from '../assets/theme/Colors';
import { getMsgReadStatus } from '../context/MainProvider';
import { onAddClickStat } from '../services/UserService';
import { getUserEmail, getUserId } from '../Utils/AuthUtil';
import {
	confirmDiscardFacebookChatDraft,
	notifyFacebookChatNavigation,
	setFacebookChatDraftDirty,
} from '../Utils/NavigationGuard';

interface CustomDrawerProps {
	tabName: string;
	listItems: Array<NavigationItem>;
	onClickTab: (value: string) => void;
	onClose?: () => void;
	onClickLogout?: () => void;
	isScreenInMobileMode?: boolean;
	isCollapsed?: boolean;
	onToggleCollapse?: () => void;
}

interface NavigationItem {
	name: string;
	route: string;
	unread?: boolean;
	children?: Array<{
		name: string;
		route: string;
	}>;
}

function SidebarToggleIcon({ isCollapsed }: { isCollapsed: boolean }) {
	return (
		<svg
			width="18"
			height="18"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<rect
				x="3.5"
				y="4.5"
				width="17"
				height="15"
				rx="3"
				stroke={MAIN_THEME_COLOR}
				strokeWidth="1.8"
			/>
			<path
				d="M9 5V19"
				stroke={MAIN_THEME_COLOR}
				strokeWidth="1.8"
				strokeLinecap="round"
			/>
			<path
				d={isCollapsed ? 'M13 9L16 12L13 15' : 'M16 9L13 12L16 15'}
				stroke={MAIN_THEME_COLOR}
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function CustomDrawer({
	tabName,
	listItems,
	onClickTab,
	onClose,
	onClickLogout,
	isScreenInMobileMode = false,
	isCollapsed = false,
	onToggleCollapse,
}: CustomDrawerProps) {
	const drawerWidth = isCollapsed ? 72 : 240;
	const navigate = useNavigate();
	const [expandedMenus, setExpandedMenus] = useState<Array<string>>([]);

	function getIconColor(selectedTabName: string, route: string) {
		if (
			selectedTabName === route ||
			selectedTabName.substring(0, 8) === route
		) {
			return MAIN_THEME_COLOR;
		} else {
			return '#7C7C7C';
		}
	}

	function isSelectedRoute(route: string) {
		return tabName === route || tabName.substring(0, 8) === route;
	}

	function hasSelectedChild(item: NavigationItem) {
		return item.children?.some(child => tabName === child.route) || false;
	}

	function isExpanded(item: NavigationItem) {
		return expandedMenus.includes(item.route);
	}

	function onClickParentItem(item: NavigationItem) {
		if (isCollapsed) {
			requestNavigation(item.children?.[0]?.route || item.route);
			return;
		}

		if (isExpanded(item)) {
			setExpandedMenus(value =>
				value.filter(route => route !== item.route),
			);
			return;
		}

		setExpandedMenus(value => [...value, item.route]);
		requestNavigation(item.children?.[0]?.route || item.route);
	}

	function onToggleParentItem(item: NavigationItem) {
		setExpandedMenus(value =>
			value.includes(item.route)
				? value.filter(route => route !== item.route)
				: [...value, item.route],
		);
	}

	function requestNavigation(route: string) {
		if (!confirmDiscardFacebookChatDraft()) {
			return;
		}

		navigateToRoute(route);
	}

	function navigateToRoute(route: string) {
		setFacebookChatDraftDirty(false);
		notifyFacebookChatNavigation(route);
		onClickTab(route);
		navigate(route);
		onClose?.();
	}

	useEffect(() => {
		const selectedParentRoutes = listItems
			.filter(item => hasSelectedChild(item))
			.map(item => item.route);

		setExpandedMenus(value =>
			Array.from(new Set([...value, ...selectedParentRoutes])),
		);
	}, [tabName, listItems]);

	return (
		<Drawer
			sx={{
				width: isScreenInMobileMode ? '100%' : drawerWidth,
				boxSizing: 'border-box',
				borderWidth: '0px',
				marginTop: isScreenInMobileMode ? '0px' : '50px',
				height: isScreenInMobileMode ? '100%' : '95%',
				flexShrink: 0,
				'& .MuiDrawer-paper': {
					width: isScreenInMobileMode ? '100%' : drawerWidth,
					boxSizing: 'border-box',
					overflowX: 'hidden',
					transition: 'width 0.2s ease',
				},
			}}
			variant="permanent"
			anchor="left"
			elevation={3}>
			{isScreenInMobileMode && (
				<Toolbar
					style={{
						paddingLeft: '20px',
						minHeight: '52px',
						justifyContent: 'space-between',
					}}>
					<Typography
						fontSize={'18px'}
						fontWeight={'700'}
						color={MAIN_THEME_COLOR}
						paddingLeft="5px">
						AskAI
					</Typography>
					<Grid2
						sx={{
							flexDirection: 'column',
						}}>
						<Button
							sx={{
								marginRight: '5px',
								color: MAIN_THEME_COLOR,
								textTransform: 'none',
							}}
							onClick={onClickLogout}>
							Logout
						</Button>
						<IconButton onClick={onClose}>
							<CloseIcon />
						</IconButton>
					</Grid2>
				</Toolbar>
			)}
			{!isScreenInMobileMode && (
				<Grid2
					container
					justifyContent={isCollapsed ? 'center' : 'flex-end'}
					paddingTop="10px"
					paddingRight={isCollapsed ? '0px' : '18px'}>
					<Tooltip
						title={isCollapsed ? 'Open sidebar' : 'Close sidebar'}
						placement="right">
						<IconButton
							size="small"
							onClick={onToggleCollapse}
							sx={{
								width: 36,
								height: 36,
								border: '1px solid #D8E7E4',
								borderRadius: '8px',
								backgroundColor: '#FFFFFF',
								boxShadow: '0px 2px 8px rgba(15, 23, 42, 0.05)',
								transition:
									'background-color 0.2s ease, border-color 0.2s ease',
								'&:hover': {
									backgroundColor: '#EBFAF8',
									borderColor: '#BDEFE1',
								},
							}}>
							<SidebarToggleIcon isCollapsed={isCollapsed} />
						</IconButton>
					</Tooltip>
				</Grid2>
			)}
			<List style={{ marginTop: isCollapsed ? '8px' : '15px' }}>
				{listItems.map((item, index) => {
					const hasChildren = Boolean(item.children?.length);
					const isParentSelected =
						isSelectedRoute(item.route) || hasSelectedChild(item);

					return (
						<Box key={index}>
							<ListItem
								disablePadding
								style={{
									paddingLeft: isCollapsed ? '12px' : '20px',
									paddingRight: isCollapsed ? '12px' : '20px',
									minHeight: '52px',
									justifyContent: isCollapsed
										? 'center'
										: 'space-between',
								}}>
								<ListItemButton
									sx={{
										justifyContent: isCollapsed
											? 'center'
											: 'space-between',
										backgroundColor: isParentSelected
											? '#EBFAF8'
											: 'transparent',
										borderRadius: '6px',
										minHeight: '36px',
										paddingX: isCollapsed ? '0px' : '10px',
										width: isCollapsed ? '48px' : '100%',
										'&:hover': {
											backgroundColor: isParentSelected
												? '#EBFAF8'
												: '#F7FBFB',
										},
									}}
									onClick={() =>
										hasChildren
											? onClickParentItem(item)
											: requestNavigation(item.route)
									}>
									{isCollapsed ? (
										<Typography
											fontSize="15px"
											fontWeight="600"
											color={
												isParentSelected
													? MAIN_THEME_COLOR
													: '#7C7C7C'
											}>
											{item.name.charAt(0)}
										</Typography>
									) : (
										<Typography
											style={{
												width: '90%',
											}}>
											{item.name}
										</Typography>
									)}
									{hasChildren && !isCollapsed ? (
										<IconButton
											size="small"
											sx={{ padding: '2px' }}
											onClick={event => {
												event.stopPropagation();
												onToggleParentItem(item);
											}}>
											<ChevronDownIcon
												color={getIconColor(
													tabName,
													item.route,
												)}
												style={{
													transform: isExpanded(item)
														? 'rotate(180deg)'
														: 'rotate(0deg)',
													transition:
														'transform 0.2s ease',
												}}
											/>
										</IconButton>
									) : (
										item.unread && (
											<Box
												sx={{
													width: 8,
													height: 8,
													borderRadius: '50%',
													backgroundColor:
														MAIN_THEME_COLOR,
												}}
											/>
										)
									)}
								</ListItemButton>
							</ListItem>
							{hasChildren && (
								<Collapse
									in={!isCollapsed && isExpanded(item)}
									timeout="auto"
									unmountOnExit>
									<List component="div" disablePadding>
										{item.children?.map(child => (
											<ListItem
												key={child.route}
												disablePadding
												style={{
													paddingLeft: '20px',
													paddingRight: '20px',
													minHeight: '42px',
												}}>
												<ListItemButton
													sx={{
														justifyContent:
															'space-between',
														backgroundColor:
															isSelectedRoute(
																child.route,
															)
																? '#EBFAF8'
																: 'transparent',
														borderRadius: '6px',
														minHeight: '34px',
														paddingX: '10px',
														'&:hover': {
															backgroundColor:
																isSelectedRoute(
																	child.route,
																)
																	? '#EBFAF8'
																	: '#F7FBFB',
														},
													}}
													onClick={() =>
														requestNavigation(
															child.route,
														)
													}>
													<Typography
														fontSize="14px"
														style={{
															width: '90%',
														}}>
														{child.name}
													</Typography>
												</ListItemButton>
											</ListItem>
										))}
									</List>
								</Collapse>
							)}
						</Box>
					);
				})}
			</List>
		</Drawer>
	);
}

export default function SideNavigationBar() {
	const location = useLocation();
	const navigate = useNavigate();
	const theme = useTheme();
	const { isUnreadMsgInChat, isUnreadMsgInStudio, updateMsgStatus } =
		getMsgReadStatus();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [tabName, setTabName] = useState('/');
	const [openDrawerInMobile, setOpenDrawerInMobile] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [isCollapsed, setIsCollapsed] = useState(
		localStorage.getItem('askai-sidebar-collapsed') === 'true',
	);
	const open = Boolean(anchorEl);
	const listItems = [
		{
			name: 'Home',
			route: '/home',
			unread: false,
		},
		{
			name: 'Dashboard',
			route: '/dashboard',
			unread: false,
		},
		// {
		// 	name: 'Activity Log',
		// 	route: '/chatHistory',
		// },
		// {
		// 	name: 'Consultant Assistance',
		// 	route: '/agentRequest',
		// },
		{
			name: 'Studio',
			route: '/knowledgeUpload',
			unread: isUnreadMsgInStudio,
		},
		{
			name: 'Conversations',
			route: '/chats',
			unread: isUnreadMsgInChat,
		},
		{
			name: 'Social',
			route: '/social',
			children: [
				{
					name: 'Facebook',
					route: '/social/facebook',
				},
			],
		},
	];

	useEffect(() => {
		setTabName(location.pathname);
	}, [location.pathname]);

	useEffect(() => {
		if (isMobile) {
			document.documentElement.style.setProperty(
				'--side-nav-width',
				'0px',
			);
			return;
		}

		const width = isCollapsed ? '72px' : '230px';
		document.documentElement.style.setProperty('--side-nav-width', width);
		localStorage.setItem(
			'askai-sidebar-collapsed',
			isCollapsed ? 'true' : 'false',
		);
	}, [isCollapsed, isMobile]);

	function openDrawerInMobileMode() {
		setOpenDrawerInMobile(!openDrawerInMobile);
	}

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	function onClickLogout() {
		localStorage.clear();
		navigate('/login', { replace: true });
	}

	function onAddClickStats(route: string) {
		const item =
			listItems.find(e => e.route === route)?.name ||
			listItems
				.flatMap(e => e.children || [])
				.find(e => e.route === route)?.name;
		onAddClickStat(
			getUserId() ?? '',
			`${item} tab`,
			true,
			'Navigation Bar',
			getUserEmail() ? getUserEmail() : 'N/A',
			'N/A',
		);
	}

	if (isMobile) {
		return (
			<>
				<CssBaseline />
				<TopNavigationBar
					onClickLogo={openDrawerInMobileMode}
					onClickLogout={onClickLogout}
					anchorEl={anchorEl}
					open={open}
					handleClose={handleClose}
					handleClick={handleClick}
				/>
				{openDrawerInMobile && (
					<CustomDrawer
						tabName={tabName}
						listItems={listItems}
						onClickTab={value => {
							setTabName(value);
							updateMsgStatus(value);
							onAddClickStats(value);
						}}
						isScreenInMobileMode={isMobile}
						onClose={openDrawerInMobileMode}
						onClickLogout={onClickLogout}
					/>
				)}
			</>
		);
	} else {
		return (
			<Box sx={{ display: 'flex' }}>
				<CssBaseline />
				<TopNavigationBar
					onClickLogout={onClickLogout}
					anchorEl={anchorEl}
					open={open}
					handleClose={handleClose}
					handleClick={handleClick}
				/>
				<CustomDrawer
					tabName={tabName}
					listItems={listItems}
					onClickTab={value => {
						setTabName(value);
						updateMsgStatus(value);
						onAddClickStats(value);
					}}
					isCollapsed={isCollapsed}
					onToggleCollapse={() => setIsCollapsed(value => !value)}
				/>
			</Box>
		);
	}
}
